/**
 * RBAC hooks and components — Phase 2 frontend.
 *
 * Wrappers ergonomic autour de OrgContext. Documentés ici pour avoir
 * une API stable que les nouveaux composants peuvent utiliser.
 *
 * Exemples :
 *   const canDelete = useCanAction("delete_orders");
 *   const canWrite = useCan("orders", "write");
 *
 *   <Can module="finance" level="write">
 *     <Button onClick={save}>Enregistrer</Button>
 *   </Can>
 *
 *   <CanAction action="export_data" fallback={<DisabledExportBtn/>}>
 *     <Button onClick={exportPdf}>Exporter</Button>
 *   </CanAction>
 */
import { useOrg } from "@/context/OrgContext";

/** Returns true if the current user has at least <level> on <module>. */
export const useCan = (module, level = "read") => {
  const { hasLevel, loading } = useOrg();
  if (loading) return false;
  return hasLevel(module, level);
};

/** Returns true if the current user can perform the sensitive action. */
export const useCanAction = (action) => {
  const { canDo, loading } = useOrg();
  if (loading) return false;
  return canDo(action);
};

/** Conditional render based on module/level permissions. */
export const Can = ({ module, level = "read", children, fallback = null }) => {
  const allowed = useCan(module, level);
  return allowed ? children : fallback;
};

/** Conditional render based on a sensitive action. */
export const CanAction = ({ action, children, fallback = null }) => {
  const allowed = useCanAction(action);
  return allowed ? children : fallback;
};

/** Wraps a button-like element : if disallowed, renders disabled + tooltip. */
export const PermissionGate = ({
  module,
  level = "read",
  action,
  children,
  tooltip = "Permission insuffisante",
  hide = false,
}) => {
  const canByLevel = useCan(module || "dashboard", level);
  const canByAction = useCanAction(action || "");
  const allowed = action ? canByAction : canByLevel;
  if (allowed) return children;
  if (hide) return null;
  // Render a wrapper that disables interaction visually.
  return (
    <span
      title={tooltip}
      className="inline-flex opacity-40 cursor-not-allowed select-none pointer-events-none"
      aria-disabled="true"
    >
      {children}
    </span>
  );
};
